import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Kunde } from '../types';
import kundeService from '../services/kundeService';
import { Icon, PageHeader, LoadingScreen, EmptyState, FilterStand, FilterZuruecksetzen, AnlegenButton, filterAktiv, fehlerText } from '../components/ui';
import { useStueckweise } from '../utils/stueckweise';
import { markiereNeu, ankunft } from '../utils/ankunft';

const Kunden: React.FC = () => {
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingKunde, setEditingKunde] = useState<Kunde | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [showMailModal, setShowMailModal] = useState(false);
  const [selectedMailIds, setSelectedMailIds] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    vorname: '', nachnameFirma: '', strasse: '', plz: '', ort: '', mobiltelefon: '', email: '', entfernungKm: '', anfahrtMinuten: ''
  });

  useEffect(() => { loadKunden(); }, []);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadKunden = async () => {
    try {
      setKunden(await kundeService.getAll());
    } catch (err) { toast.error(fehlerText(err, 'Kunden konnten nicht geladen werden')); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        vorname: formData.vorname || undefined,
        strasse: formData.strasse || undefined,
        plz: formData.plz || undefined,
        ort: formData.ort || undefined,
        mobiltelefon: formData.mobiltelefon || undefined,
        email: formData.email || undefined,
        entfernungKm: formData.entfernungKm ? Number(formData.entfernungKm) : 0,
        anfahrtMinuten: formData.anfahrtMinuten ? Number(formData.anfahrtMinuten) : 0,
      };
      if (editingKunde) {
        const gespeichert = await kundeService.update(editingKunde.id, data);
        markiereNeu(`kunde-${gespeichert?.id ?? editingKunde.id}`);
        toast.success('Kunde gespeichert');
      } else {
        const angelegt = await kundeService.create(data);
        if (angelegt?.id) markiereNeu(`kunde-${angelegt.id}`);
        toast.success('Kunde angelegt');
      }
      resetForm();
      await loadKunden();
    } catch (err: any) {
      toast.error(fehlerText(err, 'Kunde konnte nicht gespeichert werden'));
    }
  };

  const handleEdit = (kunde: Kunde) => {
    setOpenMenuId(null);
    setEditingKunde(kunde);
    setFormData({
      vorname: kunde.vorname || '',
      nachnameFirma: kunde.nachnameFirma,
      strasse: kunde.strasse || '',
      plz: kunde.plz || '',
      ort: kunde.ort || '',
      mobiltelefon: kunde.mobiltelefon || '',
      email: kunde.email || '',
      entfernungKm: kunde.entfernungKm ? String(kunde.entfernungKm) : '',
      anfahrtMinuten: kunde.anfahrtMinuten ? String(kunde.anfahrtMinuten) : '',
    });
    setShowForm(true);
  };

  const handleToggleActive = async (kunde: Kunde) => {
    try {
      const updated = await kundeService.setActive(kunde.id, !kunde.active);
      setKunden(prev => prev.map(k => k.id === updated.id ? updated : k));
    } catch (err) {
      toast.error(fehlerText(err, 'Status konnte nicht geändert werden'));
    }
  };

  const handleDelete = async (kunde: Kunde) => {
    setOpenMenuId(null);
    const name = [kunde.vorname, kunde.nachnameFirma].filter(Boolean).join(' ');
    if (window.confirm(`Kunde „${name}“ löschen?`)) {
      try {
        await kundeService.delete(kunde.id);
        toast.success('Kunde gelöscht');
        await loadKunden();
      } catch (err: any) {
        toast.error(fehlerText(err, 'Kunde konnte nicht gelöscht werden'));
      }
    }
  };

  const handleBerechneAnfahrtzeiten = async () => {
    if (!window.confirm('Anfahrtzeiten für alle Kunden mit Koordinaten via Mapbox berechnen?\nDas kann einige Minuten dauern.')) return;
    try {
      const result = await kundeService.berechneAnfahrtzeiten();
      toast.success(result.message);
      await loadKunden();
    } catch (err: any) {
      toast.error(fehlerText(err, 'Anfahrtzeiten konnten nicht berechnet werden'));
    }
  };

  // Alle Kunden mit E-Mail-Adresse (egal ob aktiv), nach Name sortiert
  const mailKunden = kunden
    .filter(k => k.email && k.email.trim())
    .sort((a, b) => [a.nachnameFirma, a.vorname].join(' ').localeCompare([b.nachnameFirma, b.vorname].join(' ')));

  const openMailModal = () => {
    if (mailKunden.length === 0) {
      toast.error('Keine Kunden mit E-Mail-Adresse vorhanden');
      return;
    }
    setSelectedMailIds(new Set(mailKunden.map(k => k.id)));
    setShowMailModal(true);
  };

  const toggleMailKunde = (id: number) => {
    setSelectedMailIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSammelMail = async () => {
    const empfaenger = Array.from(new Set(
      mailKunden.filter(k => selectedMailIds.has(k.id)).map(k => k.email!.trim())
    ));
    if (empfaenger.length === 0) {
      toast.error('Keine Empfänger ausgewählt');
      return;
    }
    // Adressen (mit "; " getrennt – Outlook-Format) in die Zwischenablage legen,
    // damit sie z.B. ins BCC-Feld eingefügt werden können.
    try {
      await navigator.clipboard.writeText(empfaenger.join('; '));
      setShowMailModal(false);
      toast.success(`${empfaenger.length} Adressen in die Zwischenablage kopiert`);
    } catch {
      toast.error('Adressen konnten nicht kopiert werden');
    }
  };

  const handleCsvImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await kundeService.importCsv(file);
      toast.success(result.message);
      await loadKunden();
    } catch (err: any) {
      toast.error(fehlerText(err, 'CSV-Datei konnte nicht importiert werden'));
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetForm = () => {
    setFormData({ vorname: '', nachnameFirma: '', strasse: '', plz: '', ort: '', mobiltelefon: '', email: '', entfernungKm: '', anfahrtMinuten: '' });
    setEditingKunde(null);
    setShowForm(false);
  };

  const filteredKunden = useMemo(() => kunden.filter(k => {
    const term = searchTerm.toLowerCase();
    return (
      (k.vorname || '').toLowerCase().includes(term) ||
      k.nachnameFirma.toLowerCase().includes(term) ||
      (k.ort || '').toLowerCase().includes(term) ||
      (k.email || '').toLowerCase().includes(term)
    );
  }), [kunden, searchTerm]);

  const { sichtbar, Ende } = useStueckweise(filteredKunden);
  const filterIstAktiv = searchTerm.trim() !== '';

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-4">
        <PageHeader
          title="Kunden"
          actions={<AnlegenButton onClick={() => setShowForm(true)}>Kunde anlegen</AnlegenButton>}
          className="mb-3"
        />
        {/* Search + CSV in a row */}
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[12rem]">
            <Icon.Search className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${filterIstAktiv ? 'text-brand-600' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Suchen …"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full p-2 pl-8 border border-gray-300 rounded-lg text-sm ${filterIstAktiv ? filterAktiv : ''}`}
            />
          </div>
          <input type="file" ref={fileInputRef} accept=".csv" onChange={handleCsvImport} className="hidden" />
          <button
            onClick={() => fileInputRef.current?.click()}
            title="Kunden aus CSV-Datei importieren"
            className="text-gray-500 hover:text-gray-700 px-3 py-2 border border-gray-300 rounded-lg text-sm whitespace-nowrap flex items-center gap-1"
          >
            <Icon.Upload className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={handleBerechneAnfahrtzeiten}
            title="Anfahrtzeiten für alle Kunden mit Koordinaten berechnen"
            className="text-gray-500 hover:text-gray-700 px-3 py-2 border border-gray-300 rounded-lg text-sm whitespace-nowrap flex items-center gap-1"
          >
            <Icon.Auto className="w-4 h-4" />
            Anfahrtzeiten
          </button>
          <button
            onClick={openMailModal}
            title="E-Mail-Adressen der Kunden in die Zwischenablage kopieren"
            className="text-gray-500 hover:text-gray-700 px-3 py-2 border border-gray-300 rounded-lg text-sm whitespace-nowrap flex items-center gap-1"
          >
            <Icon.Kopieren className="w-4 h-4" />
            E-Mail an alle
          </button>
        </div>
        <div className="mt-2 flex justify-end">
          <FilterStand treffer={filteredKunden.length} gesamt={kunden.length} einheit="Kunden" aktiv={filterIstAktiv} onZuruecksetzen={() => setSearchTerm('')} />
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="ui-pop bg-white rounded-lg p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingKunde ? 'Kunde bearbeiten' : 'Kunde anlegen'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Vorname</label>
                  <input type="text" value={formData.vorname}
                    onChange={(e) => setFormData({ ...formData, vorname: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nachname / Firma *</label>
                  <input type="text" required value={formData.nachnameFirma}
                    onChange={(e) => setFormData({ ...formData, nachnameFirma: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">Straße</label>
                <input type="text" value={formData.strasse}
                  onChange={(e) => setFormData({ ...formData, strasse: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">PLZ</label>
                  <input type="text" value={formData.plz}
                    onChange={(e) => setFormData({ ...formData, plz: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Ort</label>
                  <input type="text" value={formData.ort}
                    onChange={(e) => setFormData({ ...formData, ort: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Telefon</label>
                  <input type="text" value={formData.mobiltelefon}
                    onChange={(e) => setFormData({ ...formData, mobiltelefon: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">E-Mail</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Entfernung (km)</label>
                  <input type="number" min={0} step="0.1" value={formData.entfernungKm}
                    onChange={(e) => setFormData({ ...formData, entfernungKm: e.target.value })}
                    placeholder="z.B. 25.5"
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Anfahrtzeit (Minuten)</label>
                  <input type="number" min={0} step="1" value={formData.anfahrtMinuten}
                    onChange={(e) => setFormData({ ...formData, anfahrtMinuten: e.target.value })}
                    placeholder="z.B. 30"
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={resetForm}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm">
                  Abbrechen
                </button>
                <button type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm">
                  {editingKunde ? 'Speichern' : 'Kunde anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sammel-Mail Modal */}
      {showMailModal && (
        <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="ui-pop bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold">E-Mail-Adressen kopieren</h2>
              <p className="text-sm text-gray-500 mt-1">
                Adressen an-/abwählen. Die ausgewählten werden in die Zwischenablage kopiert (z.B. zum Einfügen ins BCC-Feld).
              </p>
              <div className="flex gap-3 mt-3 text-sm">
                <button
                  type="button"
                  onClick={() => setSelectedMailIds(new Set(mailKunden.map(k => k.id)))}
                  className="text-brand-600 hover:underline"
                >
                  Alle
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedMailIds(new Set())}
                  className="text-brand-600 hover:underline"
                >
                  Keine
                </button>
                <span className="ml-auto text-gray-400">{selectedMailIds.size} von {mailKunden.length} ausgewählt</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-2">
              {mailKunden.map(kunde => {
                const name = [kunde.vorname, kunde.nachnameFirma].filter(Boolean).join(' ');
                return (
                  <label key={kunde.id} className="flex items-center gap-3 py-2 cursor-pointer border-b border-gray-50 last:border-0">
                    <input
                      type="checkbox"
                      checked={selectedMailIds.has(kunde.id)}
                      onChange={() => toggleMailKunde(kunde.id)}
                      className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-800 truncate">{name}</span>
                      <span className="block text-xs text-gray-500 truncate">{kunde.email}</span>
                    </span>
                    {!kunde.active && (
                      <span className="flex-shrink-0 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">inaktiv</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowMailModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleSammelMail}
                disabled={selectedMailIds.size === 0}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Kopieren ({selectedMailIds.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer List */}
      <div className="space-y-2">
        {sichtbar.map((kunde) => {
          const name = [kunde.vorname, kunde.nachnameFirma].filter(Boolean).join(' ');
          const address = [kunde.plz, kunde.ort].filter(Boolean).join(' ');
          const isMenuOpen = openMenuId === kunde.id;
          const a = ankunft(`kunde-${kunde.id}`);

          return (
            <div key={kunde.id} ref={a.ref} className={`bg-white rounded-lg shadow-sm border border-gray-100 ${a.className}`}>
              <div className="flex items-center">
                {/* Main content - tap to open detail */}
                <div
                  className="flex-1 min-w-0 p-3 md:p-4 cursor-pointer"
                  onClick={() => navigate(`/kunden/${kunde.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 truncate">{name}</div>
                      {address && (
                        <div className="text-xs text-gray-500 mt-0.5">{address}</div>
                      )}
                      {/* Show extra info on desktop */}
                      <div className="hidden md:flex gap-4 mt-1 text-xs text-gray-400">
                        {kunde.mobiltelefon && <span>{kunde.mobiltelefon}</span>}
                        {kunde.email && <span>{kunde.email}</span>}
                      </div>
                    </div>
                    <Icon.ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-0.5 ml-2" />
                  </div>
                </div>

                {/* Active toggle */}
                <button
                  onClick={() => handleToggleActive(kunde)}
                  title={kunde.active ? 'Aktiv – klicken zum Deaktivieren' : 'Inaktiv – klicken zum Aktivieren'}
                  className={`flex-shrink-0 w-8 h-5 rounded-full transition-colors mx-1 ${
                    kunde.active ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white shadow transform transition-transform mx-0.5 ${
                      kunde.active ? 'translate-x-3' : 'translate-x-0'
                    }`}
                  />
                </button>

                {/* Schnellaktion: Bericht anlegen */}
                <button
                  onClick={() => navigate(`/berichte/neu?kundeId=${kunde.id}`)}
                  className="flex-shrink-0 flex items-center gap-1 px-3 py-2 mr-1 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                  title="Bericht anlegen"
                >
                  <Icon.Bericht className="w-5 h-5" />
                  <span className="hidden md:inline text-sm">Bericht</span>
                </button>

                {/* Three-dot menu */}
                <div className="relative flex-shrink-0 pr-2" ref={isMenuOpen ? menuRef : undefined}>
                  <button
                    onClick={() => setOpenMenuId(isMenuOpen ? null : kunde.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Weitere Aktionen"
                  >
                    <Icon.Mehr className="w-5 h-5" />
                  </button>

                  {/* Dropdown menu */}
                  {isMenuOpen && (
                    <div className="ui-fade absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1">
                      <button
                        onClick={() => handleEdit(kunde)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Icon.Edit className="w-4 h-4 text-gray-400" />
                        Bearbeiten
                      </button>
                      {kunde.mobiltelefon && (
                        <a
                          href={`tel:${kunde.mobiltelefon}`}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          onClick={() => setOpenMenuId(null)}
                        >
                          <Icon.Telefon className="w-4 h-4 text-gray-400" />
                          Anrufen
                        </a>
                      )}
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => handleDelete(kunde)}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Icon.Trash className="w-4 h-4" />
                        Löschen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {Ende}
      </div>

      {filteredKunden.length === 0 && (
        filterIstAktiv ? (
          <EmptyState
            icon={<Icon.Search className="h-6 w-6" />}
            title="Keine Kunden gefunden"
            hint={`Kein Kunde passt zu „${searchTerm}“.`}
            action={<FilterZuruecksetzen onClick={() => setSearchTerm('')} />}
          />
        ) : (
          <EmptyState
            icon={<Icon.Kunden className="h-6 w-6" />}
            title="Noch keine Kunden"
            hint="Legen Sie den ersten Kunden an oder importieren Sie eine CSV-Datei."
            action={<AnlegenButton onClick={() => setShowForm(true)}>Kunde anlegen</AnlegenButton>}
          />
        )
      )}
    </div>
  );
};

export default Kunden;
